import {
  bundledLanguagesInfo,
  type BundledLanguageInfo,
  type LanguageRegistration,
} from 'shiki';

export const CANGJIE_LANGUAGE_ID = 'cangjie';
export const ANTLR4_LANGUAGE_ID = 'antlr4';

const CANGJIE_LANGUAGE_ALIASES = ['Cangjie', 'cj'];
const ANTLR4_LANGUAGE_ALIASES = ['ANTLR4', 'g4'];

/**
 * These grammars are vendored from zichexuelan/markit at
 * 28af66afeefca4987e9788a91d108a0596cf8516 under that repository's MIT
 * license. They are local dynamic imports, so code blocks never fetch grammar
 * data from GitCode at runtime.
 */
const loadCangjieLanguage = async () => {
  const { default: grammar } =
    await import('./grammars/cangjie.tmLanguage.json');
  return {
    default: [
      {
        ...(grammar as unknown as LanguageRegistration),
        name: CANGJIE_LANGUAGE_ID,
        aliases: CANGJIE_LANGUAGE_ALIASES,
      },
    ],
  };
};

const loadAntlr4Language = async () => {
  const { default: grammar } = await import('./grammars/antlr.tmLanguage.json');
  return {
    default: [
      {
        ...(grammar as unknown as LanguageRegistration),
        name: ANTLR4_LANGUAGE_ID,
        aliases: ANTLR4_LANGUAGE_ALIASES,
      },
    ],
  };
};

export const CODE_BLOCK_CUSTOM_LANGUAGES: BundledLanguageInfo[] = [
  {
    id: CANGJIE_LANGUAGE_ID,
    name: 'Cangjie',
    aliases: CANGJIE_LANGUAGE_ALIASES,
    import: loadCangjieLanguage,
  },
  {
    id: ANTLR4_LANGUAGE_ID,
    name: 'ANTLR4',
    aliases: ANTLR4_LANGUAGE_ALIASES,
    import: loadAntlr4Language,
  },
];

function sortCodeBlockLanguages(
  languages: readonly BundledLanguageInfo[]
): BundledLanguageInfo[] {
  return [...languages].sort((left, right) => {
    const labelComparison = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
    });
    return labelComparison || left.id.localeCompare(right.id);
  });
}

const customLanguageAliases = new Map(
  CODE_BLOCK_CUSTOM_LANGUAGES.flatMap(language =>
    [language.id, language.name, ...(language.aliases ?? [])].map(alias => [
      alias.toLowerCase(),
      language.id,
    ])
  )
);

export const DEFAULT_CODE_BLOCK_LANGUAGES = sortCodeBlockLanguages([
  ...bundledLanguagesInfo,
  ...CODE_BLOCK_CUSTOM_LANGUAGES,
]);

export function getCodeBlockLanguages(
  configuredLanguages?: readonly BundledLanguageInfo[]
): BundledLanguageInfo[] {
  if (!configuredLanguages) {
    return DEFAULT_CODE_BLOCK_LANGUAGES;
  }

  const languageIds = new Set(configuredLanguages.map(language => language.id));
  return sortCodeBlockLanguages([
    ...configuredLanguages,
    ...CODE_BLOCK_CUSTOM_LANGUAGES.filter(
      language => !languageIds.has(language.id)
    ),
  ]);
}

export function canonicalizeCodeBlockLanguage<
  T extends string | null | undefined,
>(language: T): T {
  if (typeof language !== 'string') {
    return language;
  }

  return (customLanguageAliases.get(language.toLowerCase()) ?? language) as T;
}

export function findCodeBlockLanguageInfo(
  language: string | null | undefined,
  languages: readonly BundledLanguageInfo[] = DEFAULT_CODE_BLOCK_LANGUAGES
): BundledLanguageInfo | undefined {
  if (!language) {
    return;
  }

  const canonicalLanguage = canonicalizeCodeBlockLanguage(language);
  return languages.find(
    info =>
      info.id === canonicalLanguage ||
      info.name === language ||
      info.aliases?.includes(language)
  );
}

export interface CodeBlockLanguageSelectorItem {
  aliases?: string[];
  label: string;
  name: string;
}

export function sortCodeBlockLanguageSelectorItems(
  items: readonly CodeBlockLanguageSelectorItem[],
  locale?: string
): CodeBlockLanguageSelectorItem[] {
  const collator = new Intl.Collator(locale, {
    numeric: true,
    sensitivity: 'base',
    usage: 'sort',
  });

  return items
    .map((item, index) => ({ index, item }))
    .sort((left, right) => {
      const labelComparison = collator.compare(
        left.item.label,
        right.item.label
      );
      if (labelComparison) return labelComparison;

      const nameComparison = collator.compare(left.item.name, right.item.name);
      return nameComparison || left.index - right.index;
    })
    .map(({ item }) => item);
}

export function getCodeBlockLanguageSelectorItems(
  languages: readonly BundledLanguageInfo[],
  locale?: string
): CodeBlockLanguageSelectorItem[] {
  return sortCodeBlockLanguageSelectorItems(
    languages.map(language => ({
      aliases: language.aliases,
      label: language.name,
      name: language.id,
    })),
    locale
  );
}

export function mergePersistedCodeBlockLanguageSelectorItems(
  persistedItems: unknown,
  languages: readonly BundledLanguageInfo[],
  locale?: string
): CodeBlockLanguageSelectorItem[] {
  const availableItems = getCodeBlockLanguageSelectorItems(languages, locale);
  if (!Array.isArray(persistedItems)) {
    return availableItems;
  }

  const availableItemsByName = new Map(
    availableItems.map(item => [item.name, item])
  );
  const restoredItems: CodeBlockLanguageSelectorItem[] = [];
  const restoredNames = new Set<string>();

  for (const item of persistedItems) {
    if (
      !item ||
      typeof item !== 'object' ||
      !('name' in item) ||
      typeof item.name !== 'string' ||
      restoredNames.has(item.name)
    ) {
      continue;
    }

    const availableItem = availableItemsByName.get(item.name);
    if (!availableItem) {
      continue;
    }

    restoredItems.push(availableItem);
    restoredNames.add(item.name);
  }

  // Older versions stored a click-based order in localStorage. Validate the
  // cache for backwards compatibility, but do not let it override the
  // locale-aware alphabetical menu order.
  return sortCodeBlockLanguageSelectorItems(
    [
      ...restoredItems,
      ...availableItems.filter(item => !restoredNames.has(item.name)),
    ],
    locale
  );
}
